// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-glass.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-glass.ts — GLASS's ICON SET.
//
// THE SHARED ID CONTRACT. A style does not get its own id space: it answers
// the SAME symbol ids the Blueprint sprite defines, so every call site in the
// app is unchanged and a style switch is a sprite swap, not a refactor. Two
// rules make that safe, and scripts/check-icon-refs.mjs enforces both with
// measured counts:
//
//   1. NO ORPHANS. Every id below must exist in the Blueprint sprite. An id
//      only this set defines would render for GLASS and blank for everyone
//      else — the silent-blank failure the icons gate exists to refuse.
//   2. NO FALLBACKS (owner correction 2026-09-07). A styled set used to be
//      allowed to cover only part of the id space and let icons.ts compose
//      "this set's glyph where it has one, the Blueprint glyph otherwise".
//      The owner found that state in production under Mainframe and ruled it
//      out: a styled set ships EVERY required id in its own visual language
//      or icons:check is red. GLASS covers 127/127; the fallback path stays
//      only as the gate's build-time diagnostic.
//
// THE DRAWING RULES (spec §6). Lucide-derived geometry on the 24 grid, round
// caps and joins, and NO FILLS EVER — a fill goes muddy under blur and
// destroys the dispersion read. Stroke weight is NOT set here: styles.css
// drops `.icon` to 1.25 under this style, so one number governs the whole
// set. `opacity` is used as the style's second channel — the outer aperture
// ring of a status glyph is a hairline, the mark inside it is full weight.
//
// THE THESIS. GLASS is optical instrumentation: the iris, the lens, the
// sonar sweep, the porthole. The first 22 glyphs (the optical family, the
// mode pair, the status ladder) established it; the other 105 below complete
// it.
//
// COMPLETING THE SET — the four construction rules the 105 follow, so a
// later hand can add a glyph that belongs:
//   A. OPTICAL BODY. Where a concept permits a round containing form, it
//      gets one — an aperture ring, a cylinder, a lens rim — before a box.
//      `antenna` is a mast inside concentric emission arcs, `database` is a
//      cylinder of hairline ellipses, `gear` is an aperture with radial
//      spokes, not a rosette.
//   B. GENEROUS RADII. A rectangular body is a polished slab: rx 2.4-3.6 on
//      the 24 grid (Blueprint's Lucide art uses 2), inset off the edge.
//   C. THE OPACITY CHANNEL. Every secondary contour — an inner ring, a
//      reflection sliver, a back plate, a rule inside a page — is drawn at
//      .3-.7 while the identifying silhouette stays at full weight. This is
//      the style's second channel and it is what makes a GLASS glyph read as
//      GLASS rather than as Lucide at 1.25. It is used where a glyph HAS a
//      secondary contour; a two-stroke mark has none to give, and inventing
//      one would be decoration. Those marks take rule D instead.
//   D. THE RIM. A mark too small to carry a ring (`plus`, `x`, `check`, the
//      chevrons, the arrows) carries `RIM` — the same short fragment of the
//      same aperture edge in the same corner, every time — so a row of small
//      buttons reads as one instrument rather than as loose Lucide marks.
//
// NO FILLS, still and always: `.icon` under this style is
// `stroke-width: 1.25; fill: none` (styles/glass.css), and the paint probe
// (scripts/icon-paint-probes.mjs) rasterises all 127 under that exact rule.

/** The iris — GLASS's brand mark, and the artwork behind both `helm` and
 * `ship-wheel` (Blueprint draws those with the same ship-wheel path, so the
 * set keeps them identical to each other too). Six blades stroked as chords
 * tangent to the aperture, one hairline outer ring, and the spectral sliver
 * crossing the opening. Circular, never hexagonal — the hexagon is
 * Starship's (R9) and a real lens iris is round. */
const IRIS =
  '<circle cx="12" cy="12" r="10" opacity=".5" />' +
  '<path d="M15.2 3.06L15.2 20.94" /> <path d="M21.35 10.3L5.85 19.24" />' +
  '<path d="M18.15 19.24L2.65 10.3" /> <path d="M8.8 20.94L8.8 3.06" />' +
  '<path d="M2.65 13.7L18.15 4.76" /> <path d="M5.85 4.76L21.35 13.7" />' +
  '<circle cx="12" cy="12" r="3.2" />' +
  '<path d="M4.21 15.63L19.79 8.37" opacity=".75" />';

/** The rim — a short fragment of the aperture edge, always the same arc in
 * the same corner. GLASS's signature on the marks that are too small to take
 * a full ring (rule D above): it says "lens" without competing with the mark
 * it sits behind. */
const RIM = '<path d="M19.6 16.8a8.6 8.6 0 0 1-2.8 2.8" opacity=".4" />';

/** The re-entry sweep. Blueprint draws `rotate-cw` and `tack` from the same
 * artwork, so GLASS keeps them identical to each other too (as it already
 * does for `helm`/`ship-wheel`) — an aperture arc opened at the top right,
 * with the hairline inner stop. */
const ROTATE =
  '<path d="M20.8 12a8.8 8.8 0 1 1-2.6-6.2L20.8 8.2" /> <path d="M20.8 3.4v4.8h-4.8" />' +
  ' <circle cx="12" cy="12" r="4.6" opacity=".4" />';

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are
 * added by icons.ts, so a set cannot get either wrong).
 */
export const GLASS_SYMBOLS: Record<string, string> = {
  // ---- the optical family --------------------------------------------
  helm: IRIS,
  'ship-wheel': IRIS,
  compass:
    '<circle cx="12" cy="12" r="9" /> <path d="M12 3v2.4" /> <path d="M21 12h-2.4" />' +
    ' <path d="M12 21v-2.4" /> <path d="M3 12h2.4" /> <path d="m8.2 15.8 7.6-7.6" />' +
    ' <circle cx="12" cy="12" r="1.6" />',
  telescope:
    '<path d="M4 15.5 16.5 8.2" /> <path d="M6 18.9 18.5 11.6" /> <path d="M4 15.5 6 18.9" />' +
    ' <path d="M16.5 8.2 18.5 11.6" /> <circle cx="19.9" cy="6.4" r="2.4" />' +
    ' <path d="M9.6 18.3 8 22" /> <path d="M12.4 16.6 14 22" />',
  porthole:
    '<circle cx="12" cy="12" r="9" /> <circle cx="12" cy="12" r="5.4" opacity=".6" />' +
    ' <path d="M6.9 15.2q2.55 1.6 5.1 0t5.1 0" />',
  gauge:
    '<path d="M3.6 18.4a10 10 0 1 1 16.8 0" /> <path d="m12 13.6 4.2-4.4" />' +
    ' <circle cx="12" cy="14.6" r="1.4" /> <path d="M4.9 12.2 6.5 12.8" opacity=".6" />' +
    ' <path d="M12 4.6v1.7" opacity=".6" /> <path d="M19.1 12.2 17.5 12.8" opacity=".6" />',
  sonar:
    '<circle cx="12" cy="12" r="1.6" /> <path d="M12 12 19 5" />' +
    ' <path d="M8.5 15.5a5 5 0 0 1 7-7" /> <path d="M6 18a8.5 8.5 0 0 1 12-12" opacity=".75" />' +
    ' <path d="M3.5 20.5a12 12 0 0 1 17-17" opacity=".5" />',
  buoy:
    '<circle cx="12" cy="12" r="9" /> <circle cx="12" cy="12" r="3.6" />' +
    ' <path d="m5.6 5.6 3.8 3.8" /> <path d="m14.6 14.6 3.8 3.8" />' +
    ' <path d="m18.4 5.6-3.8 3.8" /> <path d="m9.4 14.6-3.8 3.8" />',
  horizon:
    '<path d="M2.5 15.5h19" /> <path d="M12 3v6.5" /> <path d="m8.4 6.6 3.6-3.6 3.6 3.6" />' +
    ' <path d="M6 19.4a6 6 0 0 1 12 0" /> <path d="M2.5 19.4h1.6" opacity=".6" />' +
    ' <path d="M19.9 19.4h1.6" opacity=".6" />',
  eye:
    '<path d="M2.4 12a10.4 10.4 0 0 1 19.2 0 10.4 10.4 0 0 1-19.2 0Z" />' +
    ' <circle cx="12" cy="12" r="3.4" /> <circle cx="12" cy="12" r="1.2" opacity=".5" />',
  search:
    '<circle cx="10.6" cy="10.6" r="7" /> <path d="M15.8 15.8 21 21" />' +
    ' <path d="M7.6 8.4a4.2 4.2 0 0 1 3-2.6" opacity=".55" />',

  // ---- the mode pair: an aperture opening, and one closing ------------
  sun:
    '<circle cx="12" cy="12" r="4.6" /> <circle cx="12" cy="12" r="8.6" opacity=".45" />' +
    ' <path d="M12 1.6v2" /> <path d="M12 20.4v2" /> <path d="M1.6 12h2" /> <path d="M20.4 12h2" />' +
    ' <path d="m4.6 4.6 1.5 1.5" /> <path d="m17.9 17.9 1.5 1.5" />' +
    ' <path d="m19.4 4.6-1.5 1.5" /> <path d="m6.1 17.9-1.5 1.5" />',
  moon: '<circle cx="12" cy="12" r="8.6" /> <path d="M15.4 3.8a10 10 0 0 0 0 16.4" />',

  // ---- the status ladder: one aperture, seven states ------------------
  'st-ring': '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="5.6" opacity=".45" />',
  'st-half':
    '<circle cx="12" cy="12" r="9.4" /> <path d="M12 6.4a5.6 5.6 0 0 1 0 11.2" />' +
    ' <path d="M12 6.4v11.2" />',
  'st-check': '<circle cx="12" cy="12" r="9.4" /> <path d="m8.2 12.2 2.6 2.6 5-5" />',
  'st-cross': '<circle cx="12" cy="12" r="9.4" /> <path d="m15.2 8.8-6.4 6.4" /> <path d="m8.8 8.8 6.4 6.4" />',
  'st-pause': '<circle cx="12" cy="12" r="9.4" /> <path d="M10 9v6" /> <path d="M14 9v6" />',
  'st-dash':
    '<path d="M10.1 2.7a9.4 9.4 0 0 1 3.8 0" /> <path d="M13.9 21.3a9.4 9.4 0 0 1-3.8 0" />' +
    ' <path d="M17.6 4.2a9.4 9.4 0 0 1 2.5 2.5" /> <path d="M2.7 13.9a9.4 9.4 0 0 1 0-3.8" />' +
    ' <path d="M20.1 17.3a9.4 9.4 0 0 1-2.5 2.5" /> <path d="M21.3 10.1a9.4 9.4 0 0 1 0 3.8" />' +
    ' <path d="M3.9 6.7a9.4 9.4 0 0 1 2.5-2.5" /> <path d="M6.4 19.8a9.4 9.4 0 0 1-2.5-2.5" />',
  'st-seal':
    '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="6.2" opacity=".5" />' +
    ' <path d="m9.2 12.2 2.2 2.2 4-4" />',

  // ---- the two intent marks that are not in the ladder -----------------
  'circle-alert': '<circle cx="12" cy="12" r="9.4" /> <path d="M12 7.4v5.4" /> <path d="M12 16.4h.01" />',
  'circle-check': '<circle cx="12" cy="12" r="9.4" /> <path d="m8.4 12.4 2.6 2.6 5.2-5.6" />',

  // ════════════════════════════════════════════════════════════════════
  // THE COMPLETION (owner correction 2026-09-07). Everything below is the
  // other 105 ids, re-cut in GLASS. See the "COMPLETING THE SET" note at
  // the top of this file for the four construction rules; the short
  // version is: optical bodies, generous radii, the opacity channel for
  // every secondary contour, and RIM on the marks too small to ring.
  // ════════════════════════════════════════════════════════════════════

  // ---- the rail: the eighteen glyphs the owner reads first ------------
  'square-terminal':
    '<rect x="3.4" y="3.4" width="17.2" height="17.2" rx="3.6" />' +
    ' <path d="M3.4 8.2h17.2" opacity=".4" /> <path d="m7.6 15 3-3-3-3" />' +
    ' <path d="M12.4 15h4.4" opacity=".65" />',
  library:
    '<path d="M4.4 3.6v17" /> <path d="M8.6 6.6v14" /> <path d="M12.8 5.2v15.4" />' +
    ' <path d="m16.6 5.6 4 14.6" /> <path d="M3 20.6h18.4" opacity=".5" />',
  lighthouse:
    '<circle cx="12" cy="6.4" r="2.8" /> <path d="M12 2.2v1.4" />' +
    ' <path d="M9.6 21.4 11 9.4" /> <path d="M14.4 21.4 13 9.4" />' +
    ' <path d="M8.2 21.4h7.6" /> <path d="M9.9 13.4h4.2" opacity=".6" />' +
    ' <path d="M4.4 6.4h2.2" opacity=".5" /> <path d="M17.4 6.4h2.2" opacity=".5" />' +
    ' <path d="M6.4 10.4 4.4 11.8" opacity=".4" /> <path d="m17.6 10.4 2 1.4" opacity=".4" />',
  antenna:
    '<circle cx="12" cy="10.2" r="1.8" /> <path d="M12 12.4v9" />' +
    ' <path d="M8.2 14a5.4 5.4 0 0 1 0-7.6" /> <path d="M15.8 6.4a5.4 5.4 0 0 1 0 7.6" />' +
    ' <path d="M5.4 16.8a9.4 9.4 0 0 1 0-13.2" opacity=".55" />' +
    ' <path d="M18.6 3.6a9.4 9.4 0 0 1 0 13.2" opacity=".55" />',
  activity:
    '<path d="M2.4 12h3.4l2.6-8.6 4.2 17.2 2.6-8.6h6.4" /> <path d="M2.4 12h19.2" opacity=".28" />',
  'grid-2x2':
    '<rect x="3.2" y="3.2" width="17.6" height="17.6" rx="3.6" /> <path d="M12 3.2v17.6" />' +
    ' <path d="M3.2 12h17.6" /> <rect x="6" y="6" width="4.2" height="4.2" rx="1.2" opacity=".4" />' +
    ' <rect x="13.8" y="13.8" width="4.2" height="4.2" rx="1.2" opacity=".4" />',
  container:
    '<rect x="2.6" y="6.4" width="18.8" height="11.2" rx="2.6" /> <path d="M2.6 12h18.8" opacity=".4" />' +
    ' <path d="M7 8.6v6.8" opacity=".6" /> <path d="M10.3 8.6v6.8" opacity=".6" />' +
    ' <path d="M13.7 8.6v6.8" opacity=".6" /> <path d="M17 8.6v6.8" opacity=".6" />',
  gate:
    '<rect x="4.4" y="4.4" width="15.2" height="17" rx="3" />' +
    ' <rect x="8.6" y="2.4" width="6.8" height="3.8" rx="1.6" />' +
    ' <path d="m9.2 13.4 2.2 2.2 4.4-4.6" /> <path d="M7.4 18.6h9.2" opacity=".5" />',
  'clipboard-check':
    '<rect x="4.4" y="4.4" width="15.2" height="17" rx="3" />' +
    ' <rect x="8.6" y="2.4" width="6.8" height="3.8" rx="1.6" />' +
    ' <path d="m9.2 13.4 2.2 2.2 4.4-4.6" /> <path d="M7.4 18.6h9.2" opacity=".5" />',
  'clipboard-list':
    '<rect x="4.4" y="4.4" width="15.2" height="17" rx="3" />' +
    ' <rect x="8.6" y="2.4" width="6.8" height="3.8" rx="1.6" />' +
    ' <path d="M8.4 11.6h.01" /> <path d="M8.4 16.4h.01" />' +
    ' <path d="M11.6 11.6h4.8" opacity=".65" /> <path d="M11.6 16.4h4.8" opacity=".65" />',
  'notebook-tabs':
    '<rect x="4.4" y="2.4" width="15.2" height="19.2" rx="3" /> <path d="M2.4 6.6h2.6" />' +
    ' <path d="M2.4 10.6h2.6" /> <path d="M2.4 14.6h2.6" /> <path d="M2.4 18.6h2.6" />' +
    ' <path d="M14.4 2.4v19.2" opacity=".55" /> <path d="M15.8 7.4h3" opacity=".65" />' +
    ' <path d="M15.8 12h3" opacity=".65" /> <path d="M15.8 16.6h3" opacity=".65" />',
  'git-pull-request':
    '<circle cx="6.2" cy="6.2" r="2.6" /> <circle cx="6.2" cy="6.2" r="5" opacity=".4" />' +
    ' <circle cx="17.8" cy="17.8" r="2.6" /> <circle cx="17.8" cy="17.8" r="5" opacity=".4" />' +
    ' <path d="M6.2 11.6v9.8" /> <path d="M12.8 6.2h2.8a2.2 2.2 0 0 1 2.2 2.2v4.4" />',
  siren:
    '<path d="M7.2 17.4v-5a4.8 4.8 0 0 1 9.6 0v5" />' +
    ' <rect x="4.8" y="17.4" width="14.4" height="4.2" rx="1.8" />' +
    ' <path d="M12 3.4v2" opacity=".6" /> <path d="m4.6 7.2 1.4 1.4" opacity=".6" />' +
    ' <path d="m19.4 7.2-1.4 1.4" opacity=".6" /> <path d="M2.6 12.6h1.8" opacity=".6" />' +
    ' <path d="M19.6 12.6h1.8" opacity=".6" />',
  gear:
    '<circle cx="12" cy="12" r="7.2" /> <circle cx="12" cy="12" r="5.2" opacity=".45" />' +
    ' <circle cx="12" cy="12" r="3" /> <path d="M12 1.8v3" /> <path d="M12 19.2v3" />' +
    ' <path d="M1.8 12h3" /> <path d="M19.2 12h3" /> <path d="m4.8 4.8 2.1 2.1" />' +
    ' <path d="m17.1 17.1 2.1 2.1" /> <path d="m19.2 4.8-2.1 2.1" /> <path d="m6.9 17.1-2.1 2.1" />',
  'circle-help':
    '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="6.2" opacity=".45" />' +
    ' <path d="M9.9 9.7a2.2 2.2 0 0 1 4.2.9c0 1.5-2.1 1.9-2.1 3.2" /> <path d="M12 16.8h.01" />',
  house:
    '<path d="M3.4 10.4a2.2 2.2 0 0 1 .8-1.7l6.4-5.5a2.2 2.2 0 0 1 2.8 0l6.4 5.5a2.2 2.2 0 0 1 .8 1.7v8.8a2.2 2.2 0 0 1-2.2 2.2H5.6a2.2 2.2 0 0 1-2.2-2.2Z" />' +
    ' <path d="M9.4 21.4v-6.6a1 1 0 0 1 1-1h3.2a1 1 0 0 1 1 1v6.6" opacity=".65" />',
  map:
    '<path d="M9 3.4 3.4 6.2v14.4L9 17.8l6 2.8 5.6-2.8V3.4L15 6.2Z" />' +
    ' <path d="M9 3.4v14.4" opacity=".6" /> <path d="M15 6.2v14.4" opacity=".6" />',
  'badge-check':
    '<circle cx="12" cy="11.2" r="8" /> <circle cx="12" cy="11.2" r="5.6" opacity=".45" />' +
    ' <path d="m9 11.4 2.2 2.2 4-4.2" />' +
    ' <path d="m8.2 18.2-1.6 3.4 5.4-1.7 5.4 1.7-1.6-3.4" opacity=".6" />',

  // ---- vessels, cargo, the harbour ------------------------------------
  anchor:
    '<circle cx="12" cy="4.4" r="2.2" /> <path d="M12 6.6v14.8" /> <path d="M8.6 10.2h6.8" />' +
    ' <path d="M3.6 12.6a8.4 8.4 0 0 0 16.8 0" /> <path d="m3.6 12.6 2.4 1.2" opacity=".55" />' +
    ' <path d="m20.4 12.6-2.4 1.2" opacity=".55" />',
  ship:
    '<path d="M3.6 13.8 12 10.4l8.4 3.4q-.8 4-3.4 6.6H7q-2.6-2.6-3.4-6.6Z" /> <path d="M12 2.6v7.8" />' +
    ' <path d="M6.6 12V7.2a1.6 1.6 0 0 1 1.6-1.6h7.6A1.6 1.6 0 0 1 17.4 7.2V12" opacity=".6" />' +
    ' <path d="M2.4 21.6q2.4 1.4 4.8 0t4.8 0 4.8 0 4.8 0" opacity=".5" />',
  cargo:
    '<rect x="2.6" y="12.4" width="8.4" height="8.2" rx="2" />' +
    ' <rect x="13" y="12.4" width="8.4" height="8.2" rx="2" />' +
    ' <rect x="7.8" y="3.4" width="8.4" height="8.2" rx="2" />' +
    ' <path d="M4.6 16.5h4.4" opacity=".5" /> <path d="M15 16.5h4.4" opacity=".5" />' +
    ' <path d="M9.8 7.5h4.4" opacity=".5" />',
  box:
    '<path d="M12 2.8 20.8 7.4v9.2L12 21.2 3.2 16.6V7.4Z" />' +
    ' <path d="m3.2 7.4 8.8 4.6 8.8-4.6" opacity=".6" /> <path d="M12 12v9.2" opacity=".6" />',
  crane:
    '<path d="M4.4 20.8V4.4" /> <path d="M2.4 20.8h4" /> <path d="M4.4 4.4h15.4" />' +
    ' <path d="M4.4 8.6 11.6 4.4" opacity=".5" /> <path d="M16.4 4.4v7.2" />' +
    ' <rect x="14.6" y="11.6" width="3.6" height="2.6" rx=".8" />' +
    ' <path d="M16.4 14.2v1.4a1.9 1.9 0 1 1-1.9 1.9" opacity=".7" />',
  crew:
    '<circle cx="9.4" cy="7.6" r="3.4" /> <path d="M3.2 20.6a6.2 6.2 0 0 1 12.4 0" />' +
    ' <path d="M16.4 4.6a3.4 3.4 0 0 1 0 6.6" opacity=".6" />' +
    ' <path d="M17.6 15.4a5 5 0 0 1 3.2 5.2" opacity=".6" />',
  knot:
    '<circle cx="18.6" cy="5.4" r="2.2" /> <circle cx="5.4" cy="18.6" r="2.2" />' +
    ' <path d="M6.9 17.1A13.4 13.4 0 0 1 17.1 6.9" />' +
    ' <path d="M7.4 7.4a12 12 0 0 1 9.2 9.2" opacity=".5" />',
  flag:
    '<path d="M5.4 21.4V3" /> <path d="M5.4 4.2q3.6-2 7.3 0t7.3 0v9.2q-3.6 2-7.3 0t-7.3 0Z" />' +
    ' <path d="M12.7 4.6v9" opacity=".5" />',
  wave:
    '<path d="M2 5.6q2.5 2.2 5 0t5 0 5 0 5 0" opacity=".5" />' +
    ' <path d="M2 12q2.5 2.2 5 0t5 0 5 0 5 0" />' +
    ' <path d="M2 18.4q2.5 2.2 5 0t5 0 5 0 5 0" opacity=".7" />',
  signal:
    '<path d="M6.4 21.6V3" /> <path d="M6.4 3.6 18.8 9.4 6.4 15.2Z" />' +
    ' <path d="M6.4 9.4h12.4" opacity=".45" />',
  dividers:
    '<circle cx="12" cy="4.4" r="2" /> <path d="M11.1 6.3 4.4 20.6" />' +
    ' <path d="M12.9 6.3 19.6 20.6" /> <path d="M6.6 15.8a10 10 0 0 0 10.8 0" opacity=".55" />' +
    ' <path d="M12 6.4v6" opacity=".45" />',

  // ---- the written record ---------------------------------------------
  logbook:
    '<rect x="6.4" y="2.6" width="14" height="18.8" rx="3" /> <path d="M3.6 6.4h3.4" />' +
    ' <path d="M3.6 10.6h3.4" /> <path d="M3.6 14.8h3.4" /> <path d="M3.6 19h3.4" />' +
    ' <path d="M10.4 8.4h6.6" opacity=".6" /> <path d="M10.4 12.4h6.6" opacity=".6" />' +
    ' <path d="M10.4 16.4h4.4" opacity=".6" />',
  scroll:
    '<ellipse cx="12" cy="4.8" rx="7.6" ry="2.2" /> <path d="M4.4 4.8v14.4" />' +
    ' <path d="M19.6 4.8v14.4" /> <ellipse cx="12" cy="19.2" rx="7.6" ry="2.2" opacity=".6" />' +
    ' <path d="M8.6 10h6.8" opacity=".55" /> <path d="M8.6 13.6h6.8" opacity=".55" />',
  'scroll-text':
    '<ellipse cx="12" cy="4.8" rx="7.6" ry="2.2" />' +
    ' <path d="M4.4 4.8v14.2a2.4 2.4 0 0 0 2.4 2.4h10.4a2.4 2.4 0 0 0 2.4-2.4V4.8" />' +
    ' <path d="M8.4 9.6h7.2" opacity=".6" /> <path d="M8.4 13h7.2" opacity=".6" />' +
    ' <path d="M8.4 16.4h4.8" opacity=".6" />',
  book:
    '<path d="M12 6.8v13.8" />' +
    ' <path d="M12 6.8a4 4 0 0 0-4-4H3.4v13.8H8a4 4 0 0 1 4 4" />' +
    ' <path d="M12 6.8a4 4 0 0 1 4-4h4.6v13.8H16a4 4 0 0 0-4 4" />' +
    ' <path d="M5.4 7.4h3.2" opacity=".5" /> <path d="M15.4 7.4h3.2" opacity=".5" />',
  'book-open':
    '<path d="M12 6.8v13.8" />' +
    ' <path d="M12 6.8a4 4 0 0 0-4-4H3.4v13.8H8a4 4 0 0 1 4 4" />' +
    ' <path d="M12 6.8a4 4 0 0 1 4-4h4.6v13.8H16a4 4 0 0 0-4 4" />' +
    ' <path d="M5.4 7.4h3.2" opacity=".5" /> <path d="M15.4 7.4h3.2" opacity=".5" />',
  chart:
    '<path d="M3.6 3.4v14.8a2.2 2.2 0 0 0 2.2 2.2h14.6" />' +
    ' <path d="m7.4 15.4 3.6-3.8 3 3 5-5.6" /> <circle cx="11" cy="11.6" r="1.1" opacity=".55" />' +
    ' <circle cx="19" cy="9" r="1.1" opacity=".55" />',
  scale:
    '<path d="M12 3.4v18.2" /> <path d="M7.4 21.6h9.2" />' +
    ' <path d="M3.4 7h1.2a17 17 0 0 0 7.4-1.8A17 17 0 0 0 19.4 7h1.2" />' +
    ' <path d="M5.4 7.4 2.6 15.4a4.6 4.6 0 0 0 5.6 0Z" opacity=".7" />' +
    ' <path d="m18.6 7.4 2.8 8a4.6 4.6 0 0 1-5.6 0Z" opacity=".7" />',

  // ---- the instruments ------------------------------------------------
  microscope:
    '<path d="M3.4 21.6h17.2" /> <path d="M6.6 18.4h7.8" />' +
    ' <path d="M13.6 21.6a6.6 6.6 0 0 0 0-13.2" />' +
    ' <path d="M9.4 12.4a2 2 0 0 1-2-2V6.4h5.6v4a2 2 0 0 1-2 2Z" />' +
    ' <path d="M12 6.4V3.6a1 1 0 0 0-1-1H9.2a1 1 0 0 0-1 1v2.8" />' +
    ' <path d="M9.2 14.6h2.4" opacity=".6" /> <circle cx="10.2" cy="9.2" r="1" opacity=".55" />',
  'flask-conical':
    '<path d="M9.6 2.6v6a2 2 0 0 1-.25 1L4.2 19.4a1.6 1.6 0 0 0 1.4 2.4h12.8a1.6 1.6 0 0 0 1.4-2.4l-5.15-9.8a2 2 0 0 1-.25-1v-6" />' +
    ' <path d="M8.4 2.6h7.2" /> <path d="M6.6 15.4h10.8" opacity=".6" />' +
    ' <path d="M9.4 18.4h2.4" opacity=".45" />',
  stethoscope:
    '<path d="M5.4 2.6v2" /> <path d="M10.6 2.6v2" />' +
    ' <path d="M4.6 4.6h-.8A1.8 1.8 0 0 0 2 6.4v3.2a6 6 0 0 0 12 0V6.4a1.8 1.8 0 0 0-1.8-1.8h-.8" />' +
    ' <path d="M8 15.4a6 6 0 0 0 12 0v-2.6" /> <circle cx="20" cy="10.6" r="2.2" />' +
    ' <circle cx="20" cy="10.6" r="4" opacity=".38" />',
  hammer:
    '<path d="m14.6 11.4-9 9a2 2 0 0 1-2.9-2.9l9-9" /> <path d="M9.8 6.6 13 3.4l7.6 7.6-3.2 3.2Z" />' +
    ' <path d="m12.2 8.6 3.2 3.2" opacity=".55" />',
  wrench:
    '<path d="M16.4 2.6a5.8 5.8 0 0 0-4.6 8.6l-8.2 8.2a2.2 2.2 0 0 0 3.2 3l8.2-8.2a5.8 5.8 0 0 0 6.4-8.2l-3.6 3.6-3.2-3.2Z" />' +
    ' <path d="m6.6 17.4 1.6 1.6" opacity=".5" />',
  hourglass:
    '<path d="M5.6 2.6h12.8" /> <path d="M5.6 21.4h12.8" />' +
    ' <path d="M7.4 2.6v3.6a2 2 0 0 0 .6 1.4L12 12l-4 4.4a2 2 0 0 0-.6 1.4v3.6" />' +
    ' <path d="M16.6 2.6v3.6a2 2 0 0 1-.6 1.4L12 12l4 4.4a2 2 0 0 1 .6 1.4v3.6" />' +
    ' <path d="M9.2 19.4h5.6" opacity=".6" />',
  clock:
    '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="6.2" opacity=".4" />' +
    ' <path d="M12 6.8v5.4l3.6 1.9" />',
  puzzle:
    '<path d="M9.6 3.4a2.4 2.4 0 0 1 4.8 0v1.2h3.4a1.4 1.4 0 0 1 1.4 1.4v3H20a2.4 2.4 0 0 1 0 4.8h-1.2v3a1.4 1.4 0 0 1-1.4 1.4h-3.4v-1.2a2.4 2.4 0 0 0-4.8 0v1.2H5.6a1.4 1.4 0 0 1-1.4-1.4v-3H3a2.4 2.4 0 0 1 0-4.8h1.2V6a1.4 1.4 0 0 1 1.4-1.4h4Z" />' +
    ' <path d="M9.6 9.4h4.8" opacity=".38" />',
  star:
    '<path d="m12 3.2 2.7 5.8 6.3.9-4.6 4.4 1.1 6.3-5.5-3-5.5 3 1.1-6.3L3 9.9l6.3-.9Z" />' +
    ' <circle cx="12" cy="12.4" r="2.6" opacity=".4" />',
  zap:
    '<path d="M13.4 2.6 4.6 13.4h6.2l-1 8 8.8-10.8h-6.2Z" /> <path d="M10.8 13.4h2.8" opacity=".45" />',
  shield:
    '<path d="M12 21.6c-4.6-1.6-8-4-8-8.6V5.8a1 1 0 0 1 1-1c2 0 4.6-1.2 6.4-2.7a1 1 0 0 1 1.2 0C14.4 3.6 17 4.8 19 4.8a1 1 0 0 1 1 1V13c0 4.6-3.4 7-8 8.6Z" />' +
    ' <path d="M12 18.4c-3.1-1.3-5.2-2.9-5.2-5.8V7.2c1.9-.3 3.7-1 5.2-2 1.5 1 3.3 1.7 5.2 2v5.4c0 2.9-2.1 4.5-5.2 5.8Z" opacity=".45" />',
  layers:
    '<path d="M12 2.8 21.4 7.4 12 12 2.6 7.4Z" />' +
    ' <path d="m2.6 12 9.4 4.6 9.4-4.6" opacity=".65" />' +
    ' <path d="m2.6 16.6 9.4 4.6 9.4-4.6" opacity=".4" />',
  bell:
    '<path d="M7.4 15.6V9.4a4.6 4.6 0 0 1 9.2 0v6.2" /> <path d="M5.6 15.6h12.8" />' +
    ' <path d="M10.2 18.6a2 2 0 0 0 3.6 0" /> <path d="M12 4.2V2.6" opacity=".6" />' +
    ' <path d="M3.6 12.6h1.8" opacity=".5" /> <path d="M18.6 12.6h1.8" opacity=".5" />',
  bug:
    '<path d="M7.4 11.4a4.6 4.6 0 0 1 9.2 0v3.2a4.6 4.6 0 0 1-9.2 0Z" /> <path d="M12 20.2v-9" />' +
    ' <path d="M9.2 7.4V6.4a2.8 2.8 0 0 1 5.6 0v1" /> <path d="M7.4 12.4H3.6" opacity=".6" />' +
    ' <path d="M16.6 12.4h3.8" opacity=".6" /> <path d="m7.6 8.4-2.8-2.4" opacity=".6" />' +
    ' <path d="m16.4 8.4 2.8-2.4" opacity=".6" /> <path d="m7.6 17-2.8 2.4" opacity=".6" />' +
    ' <path d="m16.4 17 2.8 2.4" opacity=".6" />',
  workflow:
    '<rect x="3.2" y="3.2" width="7.6" height="7.6" rx="2.4" />' +
    ' <rect x="13.2" y="13.2" width="7.6" height="7.6" rx="2.4" />' +
    ' <path d="M7 11.4v3.4a2.4 2.4 0 0 0 2.4 2.4h3.4" />' +
    ' <rect x="5.4" y="5.4" width="3.2" height="3.2" rx="1" opacity=".45" />',

  // ---- the machine room -----------------------------------------------
  server:
    '<rect x="2.6" y="3" width="18.8" height="7.4" rx="2.4" />' +
    ' <rect x="2.6" y="13.6" width="18.8" height="7.4" rx="2.4" />' +
    ' <path d="M6.4 6.7h.01" /> <path d="M6.4 17.3h.01" />' +
    ' <path d="M10.4 6.7h6.6" opacity=".5" /> <path d="M10.4 17.3h6.6" opacity=".5" />',
  database:
    '<ellipse cx="12" cy="5.4" rx="8.4" ry="2.8" />' +
    ' <path d="M3.6 5.4v13.2a8.4 2.8 0 0 0 16.8 0V5.4" />' +
    ' <path d="M3.6 12a8.4 2.8 0 0 0 16.8 0" opacity=".55" />' +
    ' <path d="M3.6 15.6a8.4 2.8 0 0 0 16.8 0" opacity=".35" />',
  terminal:
    '<path d="m4.6 17.4 6-6-6-6" /> <path d="M12.4 19.4h7" />' +
    ' <path d="m6.4 15.6 4-4" opacity=".3" />',
  'git-branch':
    '<circle cx="18" cy="6.2" r="2.6" /> <circle cx="18" cy="6.2" r="5" opacity=".4" />' +
    ' <circle cx="6" cy="17.8" r="2.6" /> <circle cx="6" cy="17.8" r="5" opacity=".4" />' +
    ' <path d="M6 15.2V3.2" /> <path d="M15.4 6.2A9.4 9.4 0 0 0 6 15.2" />',
  'git-merge':
    '<circle cx="6.2" cy="6.2" r="2.6" /> <circle cx="6.2" cy="6.2" r="5" opacity=".4" />' +
    ' <circle cx="17.8" cy="17.8" r="2.6" /> <circle cx="17.8" cy="17.8" r="5" opacity=".4" />' +
    ' <path d="M6.2 21.4V10.6a7.6 7.6 0 0 0 7.6 7.6" />',
  'rotate-cw': ROTATE,
  tack: ROTATE,
  'refresh-cw':
    '<path d="M3.2 12a8.8 8.8 0 0 1 15-6.2L21 8.2" /> <path d="M21 3.4v4.8h-4.8" />' +
    ' <path d="M20.8 12a8.8 8.8 0 0 1-15 6.2L3 15.8" /> <path d="M3 20.6v-4.8h4.8" />' +
    ' <circle cx="12" cy="12" r="4.4" opacity=".35" />',
  history:
    '<path d="M3.2 12a8.8 8.8 0 1 0 2.6-6.2L3.2 8.2" /> <path d="M3.2 3.4v4.8H8" />' +
    ' <path d="M12 7.2v5l3.4 1.8" /> <circle cx="12" cy="12" r="4.6" opacity=".32" />',
  'list-restart':
    '<path d="M3 5.2h18" /> <path d="M3 12h4.6" opacity=".7" /> <path d="M3 18.8h4.6" opacity=".7" />' +
    ' <path d="M12 18.2a5 5 0 0 0 9-3 4.6 4.6 0 0 0-7.9-3.2L11 14.2" /> <path d="M11 10.2v4h4" />',

  // ---- files, mail, people --------------------------------------------
  'folder-open':
    '<path d="M3 19.4V5.4a2 2 0 0 1 2-2h3.6a2 2 0 0 1 1.6.8l1 1.4a2 2 0 0 0 1.6.8H18a2 2 0 0 1 2 2v1.6" />' +
    ' <path d="m5.6 20.6 2-6a2 2 0 0 1 1.9-1.4h10.6a1.4 1.4 0 0 1 1.35 1.8l-1.6 5.2a2 2 0 0 1-1.9 1.4H5a2 2 0 0 1-2-2" />',
  'file-pen':
    '<path d="M12.6 21.4H18a2 2 0 0 0 2-2V8.6L14.4 3H6a2 2 0 0 0-2 2v8.4" />' +
    ' <path d="M14 3v4.6a1 1 0 0 0 1 1h4.6" opacity=".6" />' +
    ' <path d="M10.6 12.8a1.4 1.4 0 0 1 2 2l-4.6 4.6-2.8.8.8-2.8Z" />',
  camera:
    '<rect x="2.6" y="6.4" width="18.8" height="14" rx="3.2" />' +
    ' <path d="m8.4 6.4 1.2-2.4a1.8 1.8 0 0 1 1.6-1h1.6a1.8 1.8 0 0 1 1.6 1l1.2 2.4" />' +
    ' <circle cx="12" cy="13.4" r="4" /> <circle cx="12" cy="13.4" r="1.8" opacity=".5" />',
  mail:
    '<rect x="2.6" y="4.4" width="18.8" height="15.2" rx="3" />' +
    ' <path d="m2.6 7.4 8.3 5.3a2 2 0 0 0 2.2 0l8.3-5.3" />' +
    ' <path d="m2.6 17.4 6-4.4" opacity=".4" /> <path d="m21.4 17.4-6-4.4" opacity=".4" />',
  'user-plus':
    '<circle cx="9.4" cy="7.4" r="3.6" /> <path d="M2.8 20.8a6.6 6.6 0 0 1 13.2 0" />' +
    ' <path d="M19 8.6v5.6" /> <path d="M21.8 11.4h-5.6" />' +
    ' <circle cx="19" cy="11.4" r="4" opacity=".35" />',
  'message-square':
    '<path d="M21.4 16.4a2.2 2.2 0 0 1-2.2 2.2H8.2l-4.4 3.4a.6.6 0 0 1-1-.5V5.6a2.2 2.2 0 0 1 2.2-2.2h14.2a2.2 2.2 0 0 1 2.2 2.2Z" />' +
    ' <path d="M8 8.8h8" opacity=".55" /> <path d="M8 13h5.4" opacity=".55" />',
  'square-pen':
    '<path d="M12.4 3.4H5.4a2 2 0 0 0-2 2v13.2a2 2 0 0 0 2 2h13.2a2 2 0 0 0 2-2v-7" />' +
    ' <path d="M18.4 2.8a1.6 1.6 0 0 1 2.8 2.8l-8.6 8.6-3.6 1 1-3.6Z" />' +
    ' <path d="m17.4 5.6 1.4 1.4" opacity=".5" />',
  bookmark:
    '<path d="M6.4 3.4h11.2a1.4 1.4 0 0 1 1.4 1.4v16l-7-4-7 4v-16a1.4 1.4 0 0 1 1.4-1.4Z" />' +
    ' <path d="M8.6 7.6h6.8" opacity=".5" />',
  link:
    '<path d="M9.8 13.4a4.8 4.8 0 0 0 7.2.5l2.8-2.8a4.8 4.8 0 0 0-6.8-6.8l-1.6 1.6" />' +
    ' <path d="M14.2 10.6a4.8 4.8 0 0 0-7.2-.5l-2.8 2.8a4.8 4.8 0 0 0 6.8 6.8l1.6-1.6" />' +
    ' <path d="m10.4 13.6 3.2-3.2" opacity=".35" />',
  filter:
    '<path d="M3.4 4.6h17.2l-6.6 7.6v7.6l-4-2.2v-5.4Z" /> <path d="M6.6 7.6h10.8" opacity=".45" />',
  'trash-2':
    '<path d="M4.4 6.4h15.2" /> <path d="M18.6 6.4v13a2 2 0 0 1-2 2H7.4a2 2 0 0 1-2-2v-13" />' +
    ' <path d="M8.6 6.4V4.6a1.8 1.8 0 0 1 1.8-1.8h3.2a1.8 1.8 0 0 1 1.8 1.8v1.8" />' +
    ' <path d="M10.2 10.6v6.6" opacity=".6" /> <path d="M13.8 10.6v6.6" opacity=".6" />',
  pencil:
    '<path d="M17.4 3.2a2.2 2.2 0 0 1 3.4 2.8L8.4 18.4l-4.6 1.4 1.4-4.6Z" />' +
    ' <path d="m15.6 5 3.4 3.4" opacity=".55" />',
  ban:
    '<circle cx="12" cy="12" r="9.4" /> <path d="m5.4 5.4 13.2 13.2" />' +
    ' <circle cx="12" cy="12" r="6" opacity=".35" />',
  pin:
    '<path d="M12 16.6v5" />' +
    ' <path d="M9.4 10.4a2 2 0 0 1-1.1 1.8l-1.8.9a2 2 0 0 0-1.1 1.8v.5a1 1 0 0 0 1 1h11.2a1 1 0 0 0 1-1v-.5a2 2 0 0 0-1.1-1.8l-1.8-.9a2 2 0 0 1-1.1-1.8V6.6a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8.4a2 2 0 0 0 0 4 1 1 0 0 1 1 1Z" />',
  copy:
    '<rect x="8.4" y="8.4" width="12.2" height="12.2" rx="3" />' +
    ' <path d="M15.6 5.4a2.4 2.4 0 0 0-2.4-2.4H5.8a2.4 2.4 0 0 0-2.4 2.4v7.4a2.4 2.4 0 0 0 2.4 2.4" opacity=".6" />',
  'panels-top-left':
    '<rect x="3.2" y="3.2" width="17.6" height="17.6" rx="3.6" /> <path d="M3.2 9h17.6" />' +
    ' <path d="M9 20.8V9" opacity=".7" />',
  square:
    '<rect x="3.4" y="3.4" width="17.2" height="17.2" rx="3.6" />' +
    ' <rect x="6.6" y="6.6" width="10.8" height="10.8" rx="2.2" opacity=".35" />',
  play: '<circle cx="12" cy="12" r="9.4" opacity=".45" /> <path d="M9.6 7.4 17 12l-7.4 4.6Z" />',
  assistant:
    '<path d="M12 3c0 4.97 4.03 9 9 9-4.97 0-9 4.03-9 9 0-4.97-4.03-9-9-9 4.97 0 9-4.03 9-9Z" />' +
    ' <circle cx="12" cy="12" r="2.6" opacity=".5" />',
  ellipsis:
    '<circle cx="5.4" cy="12" r="1.2" /> <circle cx="12" cy="12" r="1.2" />' +
    ' <circle cx="18.6" cy="12" r="1.2" /> ' + RIM,
  'circle-dot':
    '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="5.6" opacity=".4" />' +
    ' <circle cx="12" cy="12" r="1.6" />',
  'circle-x':
    '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="6" opacity=".35" />' +
    ' <path d="m15.2 8.8-6.4 6.4" /> <path d="m8.8 8.8 6.4 6.4" />',
  'circle-arrow-up':
    '<circle cx="12" cy="12" r="9.4" /> <circle cx="12" cy="12" r="6" opacity=".3" />' +
    ' <path d="M12 16.6V7.8" /> <path d="m8.2 11.6 3.8-3.8 3.8 3.8" />',

  // ---- the pure marks: too small to ring, so they carry the RIM -------
  plus: '<path d="M5.2 12h13.6" /> <path d="M12 5.2v13.6" /> ' + RIM,
  x: '<path d="m6.6 6.6 10.8 10.8" /> <path d="m17.4 6.6-10.8 10.8" /> ' + RIM,
  check: '<path d="m4.6 12.6 5 5 9.8-11" /> ' + RIM,
  chev: '<path d="m9.6 17.6 5.6-5.6-5.6-5.6" /> ' + RIM,
  'chevron-down': '<path d="m6.4 9.2 5.6 5.6 5.6-5.6" /> ' + RIM,
  'chevron-up': '<path d="m17.6 14.8-5.6-5.6-5.6 5.6" /> ' + RIM,
  'chevron-left': '<path d="m14.4 17.6-5.6-5.6 5.6-5.6" /> ' + RIM,
  'chevrons-right':
    '<path d="m6.4 17.2 5.2-5.2-5.2-5.2" /> <path d="m13.2 17.2 5.2-5.2-5.2-5.2" opacity=".6" />',
  'arrow-left': '<path d="M19.4 12H4.6" /> <path d="m11 4.6-6.4 7.4 6.4 7.4" /> ' + RIM,
  'arrow-right': '<path d="M4.6 12h14.8" /> <path d="m13 4.6 6.4 7.4-6.4 7.4" /> ' + RIM,
  'arrow-up-right': '<path d="M7.4 7.4h9.2v9.2" /> <path d="M7.4 16.6 16.6 7.4" /> ' + RIM,
  send:
    '<path d="M21.4 2.6 2.6 9.6l7.8 3.4 3.4 7.8Z" /> <path d="M21.4 2.6 10.4 13" opacity=".55" />',
  'log-in':
    '<path d="m10.4 16.6 4.6-4.6-4.6-4.6" /> <path d="M15 12H3.4" />' +
    ' <path d="M15 3.4h3.6a2 2 0 0 1 2 2v13.2a2 2 0 0 1-2 2H15" opacity=".65" />',
  'log-out':
    '<path d="m16.6 16.6 4.6-4.6-4.6-4.6" /> <path d="M21.2 12H9.4" />' +
    ' <path d="M9.4 20.6H5.8a2 2 0 0 1-2-2V5.4a2 2 0 0 1 2-2h3.6" opacity=".65" />',
  download:
    '<path d="M12 3.4v11.8" /> <path d="m7.4 10.6 4.6 4.6 4.6-4.6" />' +
    ' <path d="M3.4 15.6v3.2a2 2 0 0 0 2 2h13.2a2 2 0 0 0 2-2v-3.2" opacity=".65" />',
  upload:
    '<path d="M12 15.2V3.4" /> <path d="m7.4 8 4.6-4.6L16.6 8" />' +
    ' <path d="M3.4 15.6v3.2a2 2 0 0 0 2 2h13.2a2 2 0 0 0 2-2v-3.2" opacity=".65" />',
  'external-link':
    '<path d="M15 3.4h5.6V9" /> <path d="M10 14 20.6 3.4" />' +
    ' <path d="M18 13.4v5.2a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5.2" opacity=".65" />',
  'undo-2':
    '<path d="m9 14.4-5-5 5-5" /> <path d="M4 9.4h10.6a5.6 5.6 0 0 1 0 11.2H11" />' +
    ' <path d="M14.6 12.6a2.8 2.8 0 0 1 0 5.6" opacity=".4" />',
  reply:
    '<path d="M20.4 18.4v-2.2a4 4 0 0 0-4-4H4" /> <path d="m9 17.2-5-5 5-5" />' +
    ' <path d="M16.4 14.4a1.8 1.8 0 0 1 1.8 1.8" opacity=".4" />',
  'maximize-2':
    '<path d="M15 3.4h5.6V9" /> <path d="m20.6 3.4-6.6 6.6" />' +
    ' <path d="m3.4 20.6 6.6-6.6" opacity=".65" /> <path d="M9 20.6H3.4V15" opacity=".65" />',
  'minimize-2':
    '<path d="m14 10 6.6-6.6" /> <path d="M20 10h-6V4" />' +
    ' <path d="m3.4 20.6 6.6-6.6" opacity=".65" /> <path d="M4 14h6v6" opacity=".65" />',

  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose':
    '<rect x="4.4" y="3.2" width="15.2" height="17.6" rx="3.2" /> <path d="M8 8.4h8" opacity=".6" />' +
    ' <path d="M8 12h8" opacity=".6" /> <path d="M8 15.6h5.2" opacity=".6" />',
  'rs-paired-lists':
    '<rect x="2.8" y="4.2" width="8" height="15.6" rx="2.8" />' +
    ' <rect x="13.2" y="4.2" width="8" height="15.6" rx="2.8" /> <path d="M5.4 9h2.8" opacity=".5" />' +
    ' <path d="M5.4 12.6h2.8" opacity=".5" /> <path d="M15.8 9h2.8" opacity=".5" />' +
    ' <path d="M15.8 12.6h2.8" opacity=".5" />',
  'rs-table':
    '<rect x="3.2" y="4.2" width="17.6" height="15.6" rx="3.2" /> <path d="M3.2 9.4h17.6" />' +
    ' <path d="M12 9.4v10.4" opacity=".5" /> <path d="M3.2 14.6h17.6" opacity=".4" />',
  'rs-table-expansion':
    '<rect x="3.2" y="3.6" width="17.6" height="9.2" rx="3" />' +
    ' <path d="M3.2 8.2h17.6" opacity=".5" />' +
    ' <rect x="5.6" y="15.2" width="12.8" height="5.4" rx="2.2" opacity=".45" />' +
    ' <path d="m10.4 10.2 1.6 1.6 1.6-1.6" />',
  'rs-rules':
    '<circle cx="6.2" cy="6.6" r="1.6" opacity=".55" />' +
    ' <circle cx="6.2" cy="12" r="1.6" opacity=".55" />' +
    ' <circle cx="6.2" cy="17.4" r="1.6" opacity=".55" /> <path d="M10.6 6.6h9.6" />' +
    ' <path d="M10.6 12h9.6" /> <path d="M10.6 17.4h6.4" />',
  'rs-state-machine':
    '<circle cx="6.4" cy="12" r="3.6" /> <circle cx="17.6" cy="12" r="3.6" />' +
    ' <path d="M10 12h4" opacity=".6" /> <path d="m12.6 10.4 1.6 1.6-1.6 1.6" opacity=".6" />',
  'rs-spec-cards':
    '<rect x="5.4" y="8.4" width="15.4" height="12" rx="3" />' +
    ' <path d="M3.2 15.2V6.6a3 3 0 0 1 3-3h8.8" opacity=".5" />' +
    ' <path d="M8.8 12.6h8.4" opacity=".6" /> <path d="M8.8 16.2h5.2" opacity=".6" />',
  'rs-wireframe':
    '<rect x="3.2" y="4.2" width="17.6" height="15.6" rx="3.2" /> <path d="M3.2 8.6h17.6" />' +
    ' <rect x="6.4" y="11.6" width="6.2" height="4.8" rx="1.4" opacity=".5" />' +
    ' <path d="M15.2 12.4h2.8" opacity=".5" /> <path d="M15.2 15.4h2.8" opacity=".5" />',
};

/** Every id this set re-cuts. Sorted so the gate's output is stable. */
export const GLASS_ICON_IDS: string[] = Object.keys(GLASS_SYMBOLS).sort();
